import React, { useState, useEffect, useRef } from "react";
import { CSpinner, CBadge } from "@coreui/react-pro";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { editBanner, viewBanner } from "../../utils/backend";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { CImage } from "@coreui/react";

const EditBanner = () => {
  const location = useLocation();
  const { totalBanners, usedSequences = [] } = location.state || {};

  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [initialValues, setInitialValues] = useState({
    title: "",
    sequence: null,
    status: "",
    image: "",
    url_link: "",
    type: "",
  });

  const fetchData = async (id) => {
    setIsLoading(true);
    try {
      const response = await viewBanner(id);
      const fetchedValues = {
        title: response.data.title || "",
        sequence: response.data.sequence || null,
        status: response.data.status === 1 ? "true" : "false",
        image: response.data.image || "",
        url_link: response.data.url_link || "",
        type: response.data.type || "",
      };
      setInitialValues(fetchedValues);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const sequenceArray = Array.from({ length: 7 }, (_, i) => i + 1);

  const validationSchema = Yup.object().shape({
    title: Yup.string().required("Title is required"),
    sequence: Yup.number()
      .required("Sequence is required")
      .integer("Sequence must be a whole number")
      .positive("Sequence must be positive")
      .max(7, "Sequence must be 7 or less")
      .typeError("Sequence must be a number"),
    status: Yup.string()
      .required("Status is required")
      .notOneOf([""], "Please select a valid status"),
    image: Yup.mixed()
      .nullable()
      .test(
        "imageFormat",
        "Unsupported file format. Only image allowed.",
        (value) => {
          if (!value) return true;

          if (value instanceof File) {
            return [
              "image/jpeg",
              "image/png",
              "image/jpg",
              "image/gif",
              "image/webp",
              "image/svg+xml",
            ].includes(value.type);
          }

          return true;
        }
      ),
      url_link: Yup.string().when("type", {
        is: (val) => val === "link",
        then: (schema) => schema.url("Invalid URL format").required("URL Link is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
    type: Yup.string().required("Type is required").oneOf([
      "term_life", "health", "travel", "group", "property", "foreign_workers", "personal_accidents", "offshore", "trust_will", "pet","link","auto"
    ], "Invalid type"),
  });

  const handleSubmit = async (
    values,
    { setSubmitting, setErrors, setFieldTouched }
  ) => {
    setIsSubmittingForm(true);
    try {
      const formData = new FormData();

      const fieldsToCheck = ["title", "sequence", "status", "url_link", "type"];
      let hasChanges = false;
      fieldsToCheck.forEach((attribute) => {
        if (attribute === "status") {
          const status = values[attribute] === "true" ? 1 : 0;
          if (status !== initialValues[attribute]) {
            formData.append(attribute, status);
            hasChanges = true;
          }
        } else {
          if (values[attribute] !== initialValues[attribute]) {
            formData.append(attribute, values[attribute]);
            hasChanges = true;
          }
        }
      });

      if (hasChanges && !formData.has("sequence")) {
        formData.append("sequence", values["sequence"]);
      }

      if (values.image && values.image instanceof File) {
        if (
          ![
            "image/jpeg",
            "image/png",
            "image/jpg",
            "image/gif",
            "image/webp",
            "image/svg+xml",
          ].includes(values.image.type)
        ) {
          toast.error("Please upload an image file");
          return;
        }

        formData.append("image", values.image);
      }

      if (JSON.stringify(values) !== JSON.stringify(initialValues)) {
        const response = await editBanner(formData, id);
        if (response?.data) {
          toast.success("Banner updated successfully!");
          navigate("/banners/details/" + id);
        } else {
          throw new Error("Failed to update banner.");
        }
      } else {
        toast.info("No changes detected.");
        navigate("/banners");
      }
    } catch (error) {
      console.error("Failed to edit banner:", error);
      toast.error(error.message || "Failed to edit banner. Please try again.");
      setErrors({ submit: "Failed to edit banner", ...error.fieldErrors });
      Object.keys(values).forEach((field) =>
        setFieldTouched(field, true, false)
      );
    } finally {
      setSubmitting(false);
      setIsSubmittingForm(false);
    }
  };

  const handleBackClick = () => {
    const currentPath = window.location.pathname;
    if (currentPath.includes(`/details/${id}/edit`)) {
      navigate(`/banners/details/${id}`);
    } else {
      navigate("/banners");
    }
  };

  useEffect(() => {
    fetchData(id);
  }, [id]);

  return (
    <>
      {isLoading ? (
        <div className="card border-light">
          <div className="card-body p-4">
            <div className="row">
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
            </div>
            <div className="d-flex justify-content-end">
              <Skeleton
                height={40}
                width={100}
                style={{ marginRight: "10px" }}
              />
              <Skeleton height={40} width={100} />
            </div>
          </div>
        </div>
      ) : (
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, errors, touched, setFieldValue, values }) => (
            <Form>
              {isSubmittingForm && (
                <div className="form-overlay">
                  <CSpinner color="primary" />
                </div>
              )}
              <div className="card border-light">
                <div className="card-body">
                  {errors.submit && (
                    <div className="alert alert-danger">{errors.submit}</div>
                  )}
                  <div className="d-flex justify-content-center mb-5">
                    <a
                      href={`${initialValues.image}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <CImage
                        rounded
                        alt="banner-display"
                        src={`${initialValues.image}`}
                        style={{ transition: "opacity 0.3s" }}
                        className="hover-image"
                        onMouseOver={(e) => (e.target.style.opacity = "0.7")}
                        onMouseOut={(e) => (e.target.style.opacity = "1")}
                      ></CImage>
                    </a>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="title" className="form-label">
                        <span className="text-danger">*</span> Title
                      </label>{" "}
                      <Field
                        name="title"
                        as="input"
                        type="text"
                        placeholder="Enter title"
                        className={`form-control ${
                          touched.title && errors.title ? "is-invalid" : ""
                        }`}
                      />
                      <ErrorMessage
                        name="title"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="sequence" className="form-label">
                        <span className="text-danger">*</span> Sequence
                      </label>{" "}
             
                      <div className="input-group">
                        <Field
                          name="sequence"
                          as="select"
                          placeholder="Select the Sequence"
                          className={`form-select ${
                            touched.sequence && errors.sequence
                              ? "is-invalid"
                              : ""
                          }`}
                        >
                        {sequenceArray
                            .filter(
                              (num, index, self) => self.indexOf(num) === index
                            )
                            .map((num) => {
                              const status =
                              usedSequences.includes(num)

                                  ? num === initialValues.sequence
                                    ? "[Current]"
                                    : "[Taken]"
                                  : "";

                              return (
                                <option key={num} value={num}>
                                  {num} {status}
                                </option>
                              );
                            })}
                        </Field>
                      </div>
                      <ErrorMessage
                        name="sequence"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="status" className="form-label">
                        <span className="text-danger">*</span> Status
                      </label>{" "}
                      <div className="input-group">
                        <Field
                          name="status"
                          as="select"
                          className={`form-select ${
                            touched.status && errors.status ? "is-invalid" : ""
                          }`}
                        >
                          <option value="">Select a status</option>
                          <option key="Active" value="true">
                            Active
                          </option>
                          <option key="Inactive" value="false">
                            Inactive
                          </option>
           
                        </Field>
                        <ErrorMessage
                          name="status"
                          component="div"
                          className="invalid-feedback"
                        />
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="image" className="form-label">
                        Upload Image &nbsp;
                        {initialValues.image && (
                          <a
                            href={`${initialValues.image}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-link p-0"
                          >
                            <span className="badge bg-secondary">
                              Existing file:{" "}
                              {initialValues.image.split("/uploads/").pop()}
                            </span>
                          </a>
                        )}
                      </label>
                      <input
                        id="image"
                        name="image"
                        type="file"
                        ref={fileInputRef}
                        className={`form-control ${
                          touched.image && errors.image ? "is-invalid" : ""
                        }`}
                        accept="image/jpeg, image/png, image/jpg, image/gif, image/webp, image/svg+xml"
                        onChange={(event) => {
                          const image = event.currentTarget.files[0];
                          if (image) {
                            setFieldValue("image", image);
                          }
                        }}
                      />
                      <ErrorMessage
                        name="image"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                  </div>
                  <div className="row">
                  <div className="col-md-6 mb-3">
                      <label htmlFor="type" className="form-label">
                        <span className="text-danger">*</span> Type
                      </label>{" "}
                      <Field
                        name="type"
                        as="select"
                        className={`form-select ${
                          touched.type && errors.type ? "is-invalid" : ""
                        }`}
                      >
                        <option value="">Select a type</option>
                        <option value="auto">Auto</option>
                        <option value="term_life">Term Life</option>
                        <option value="health">Health</option>
                        <option value="travel">Travel</option>
                        <option value="group">Group</option>
                        <option value="property">Property</option>
                        <option value="foreign_workers">Foreign Workers</option>
                        <option value="personal_accidents">Personal Accidents</option>
                        <option value="offshore">Offshore</option>
                        <option value="trust_will">Trust Will</option>
                        <option value="pet">Pet</option>
                        <option value="link">Link</option>
                      </Field>
                      <ErrorMessage name="type" component="div" className="invalid-feedback" />
                    </div>
                    {values.type === "link" && (
                      <div className="col-md-6 mb-3">
                        <label htmlFor="url_link" className="form-label">
                          <span className="text-danger">*</span> URL Link
                        </label>
                        <Field
                          name="url_link"
                          as="input"
                          type="text"
                          placeholder="Enter URL link"
                          className={`form-control ${
                            touched.url_link && errors.url_link ? "is-invalid" : ""
                          }`}
                        />
                        <ErrorMessage name="url_link" component="div" className="invalid-feedback" />
                      </div>
                    )}
                  </div>
                  <div className=" d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn me-3 ansa-button-secondary"
                      onClick={handleBackClick}
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      className="btn ansa-button-primary"
                      disabled={isSubmitting}
                    >
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
};

export default EditBanner;
